import React, { useState, useEffect, useRef } from 'react'
import AuthLayout from '../../../../layout/AuthLayout'
import { Box, Flex, Text, Group, TextInput, Button, Pagination, Select, Modal, ScrollArea } from '@mantine/core'
import classes from '../../../../assets/module-css/AdminLayout.module.css'
import { useDispatch } from 'react-redux'
import { updateRoute } from '../../../../store/dataRoute'
import { getAllPortList, deletePort, getAllCountryList } from '../../../../services/location'
import { useDebouncedCallback } from '@mantine/hooks'
import LoadingData from '../../../../component/ui/LoadingData'
import NoData from '../../../../component/ui/NoData'
import TablePort from '../../../../component/pages/Port/TablePort'
import FormPort from '../../../../component/pages/Port/FormPort'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX, IconPlus } from '@tabler/icons-react'
import { modals } from '@mantine/modals'

const defaultParameter = {
    'page[num]': 1,
    'page[limit]': 10,
    sort: '-id',
    include: 'country'
}

const labelTable = [
    { label: 'ID', width: 80 },
    { label: 'Port Name', width: 'auto' },
    { label: 'Type', width: 150 },
    { label: 'Country', width: 250 },
    { label: 'Action', width: 100 },
]

const PortList = () => {
    const dispatch = useDispatch()
    const [params, setParams] = useState(defaultParameter)
    const [portList, setPortList] = useState([])
    const [totalData, setTotalData] = useState(0)
    const [loading, setLoading] = useState(true)
    const [openModalForm, setOpenModalForm] = useState(false)
    const [detailData, setDetailData] = useState(null)
    const [allCountries, setAllCountries] = useState([])
    const countriesRef = useRef([])

    const mappingRoute = [
        { label: '/', route: null },
        { label: 'Settings', route: null },
        { label: 'Port List', route: null },
    ]

    const handleGetAllPortList = useDebouncedCallback(async () => {
        setLoading(true)
        try {
            const response = await getAllPortList(params)

            if (response.success || response.data) { // Handle both success field or direct data
                const startData = response.data || []
                const included = response.included || []
                const countData = response.meta?.pagination?.total || 0

                // Map included countries to an object for easy lookup
                // Note: User's requirements say "include=country" (singular) but standard JSONAPI usually returns 'included' array.
                // ProvinceList mapped included.countries, so here we should check if included is array and map it. 

                const countriesMap = {}
                if (Array.isArray(included)) {
                    included.forEach(inc => {
                        if (inc.type === 'countries' || inc.type === 'country') {
                            countriesMap[inc.id] = inc
                        }
                    })
                } else if (included.countries) {
                    // ProvinceList handled it this way: included.countries[countryId] which implies included.countries is an object?
                    // But in ProvinceList code: country_name: included.countries[countryId]?.attributes?.name
                    // Let's assume standard JSON:API or similar structure as used in ProvinceList.
                    // In ProvinceList: const included = response.included || [] ... but then access included.countries ... this suggests included might be an object in that specific API response structure?
                    // Let's look at ProvinceList again. 
                    // ProvinceList: const included = response.included || []
                    // ... included.countries[countryId] ... this would fail if included is an array.
                    // IMPORTANT: The user said "similiar like province list page". 
                    // I'll stick to what ProvinceList did, assuming `response.included` returns an object with keys as types, or I should handle both.
                    // However, in standard JSONAPI `included` is an array. I will add a fallback.
                }

                // Actually, let's look at ProvinceList code again carefully.
                // line 55: const included = response.included || []
                // line 74: country_name: included.countries[countryId]?.attributes?.name || "---",
                // This implies `included` is actually an OBJECT in the response, not an array, OR `response.included` is expected to be an object map.
                // I will follow the same pattern but also handle array if needed loosely or just sticking to the pattern.

                const remapPort = startData.map((val) => {
                    let countryId = null

                    // 1. Try to find country ID from relationship
                    if (val.relationships?.country?.data?.id) {
                        countryId = val.relationships.country.data.id
                    } else if (val.relationships?.country?.id) {
                        countryId = val.relationships.country.id
                    }
                    // 2. Or from attributes
                    else if (val.attributes?.country_id) {
                        countryId = val.attributes.country_id
                    }


                    return {
                        id: val.id,
                        name: val.attributes.name,
                        port_type: val.attributes.port_type,
                        country_id: countryId,
                        country_name: included.country[countryId]?.attributes?.name || "---",
                    }
                })

                setPortList(remapPort)
                setTotalData(countData)
            } else {
                setPortList([])
                setTotalData(0)
            }
        } catch (error) {
            console.log(error)
            setPortList([])
        } finally {
            setLoading(false)
        }
    }, 500)

    useEffect(() => {
        dispatch(updateRoute({ 'data': mappingRoute }))

        const fetchCountries = async () => {
            try {
                const response = await getAllCountryList({ 'page[limit]': 300 })
                if (response && response.data) {
                    const mapped = response.data.map(c => ({
                        value: String(c.attributes.id),
                        label: c.attributes.name
                    }))
                    setAllCountries(mapped)
                    countriesRef.current = response.data
                }
            } catch (error) {
                console.error("Failed to fetch countries for mapping", error)
            }
        }
        fetchCountries()
        // eslint-disable-next-line 
    }, [dispatch])

    useEffect(() => {
        handleGetAllPortList()
        // eslint-disable-next-line
    }, [params])

    const handleDelete = (val) => {
        modals.openConfirmModal({
            title: 'Delete Port',
            children: (
                <Text size="sm">
                    Are you sure you want to delete port <strong>{val.name}</strong>? This action cannot be undone.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onCancel: () => console.log('Cancel'),
            onConfirm: async () => {
                try {
                    const response = await deletePort(val.id)
                    if (response.success) {
                        notifications.show({
                            title: 'Success',
                            message: 'Port deleted successfully',
                            color: 'teal',
                            icon: <IconCheck />,
                        })
                        handleGetAllPortList()
                    } else {
                        notifications.show({
                            title: 'Failed',
                            message: response.message || 'Failed to delete port',
                            color: 'red',
                            icon: <IconX />,
                        })
                    }
                } catch (error) {
                    notifications.show({
                        title: 'Error',
                        message: error.message || 'Something went wrong',
                        color: 'red',
                        icon: <IconX />,
                    })
                }
            },
        })
    }

    const actions = {
        edit: (val) => {
            setDetailData(val)
            setOpenModalForm(true)
        },
        delete: (val) => {
            handleDelete(val)
        }
    }

    const handleAction = (val, type) => {
        if (actions[type]) {
            actions[type](val)
        }
    }

    const handleChangeFilter = (name, val) => {
        setParams((oldVal) => ({ ...oldVal, [name]: val }))
    }

    const handleChangePage = (val) => {
        setParams((oldVal) => ({ ...oldVal, ['page[num]']: val }))
    }

    const onCloseModalForm = () => {
        setOpenModalForm(false)
        setDetailData(null)
    }

    const handleAdd = () => {
        setDetailData(null)
        setOpenModalForm(true)
    }

    return (
        <AuthLayout>
            <Box my={20}>
                <Box>
                    <Flex justify='space-between' align="center" mb={30}>
                        <Text className={classes.titleLayout}>Port List</Text>
                        <Group gap='sm' align='center'>
                            <Select
                                placeholder="Filter Country"
                                data={allCountries}
                                value={params['filter[country_id]'] || null}
                                onChange={(val) => {
                                    handleChangeFilter('filter[country_id]', val)
                                    handleChangePage(1)
                                }}
                                searchable
                                clearable
                                size='xs'
                                w={200}
                            />
                            <TextInput
                                value={params['filter[name][like]'] || ''}
                                placeholder='Search by name'
                                onChange={(val) => {
                                    handleChangeFilter('filter[name][like]', val.target.value)
                                    handleChangePage(1)
                                }}
                                size='xs'
                            />
                            <Button size='xs' leftSection={<IconPlus size={14} />} onClick={handleAdd}>Add Port</Button>
                        </Group>
                    </Flex>
                </Box>

                <Box my={30}>
                    <Group mb={10} gap='xs'>
                        <Text fz={12}>Total Data Found: </Text>
                        <Text fz={12} fw='bold'>{totalData} Ports </Text>
                    </Group>
                    {loading ? <LoadingData /> : portList.length > 0 ? <TablePort label={labelTable} data={portList} actionMethod={handleAction} /> : <NoData />}
                </Box>
                <Flex justify='end'>
                    <Pagination size='sm' onChange={handleChangePage} value={params['page[num]']} total={totalData > 0 ? Math.ceil(totalData / params['page[limit]']) : 0} />
                </Flex>
            </Box>

            <Modal opened={openModalForm} onClose={onCloseModalForm} centered closeOnClickOutside={false} size='md' title={<Text fw='Bold'>{detailData ? 'Edit Port' : 'Add Port'}</Text>} scrollAreaComponent={ScrollArea.Autosize} withCloseButton={false}>
                <FormPort dataDetail={detailData} onCloseModal={onCloseModalForm} reloadList={handleGetAllPortList} />
            </Modal>
        </AuthLayout>
    )
}

export default PortList
